
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, Input, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy, inject } from "@angular/core";

import { HeaderService } from "../../services/header.service";
import { TaskSettingsDialogComponent } from "../task-settings/task-settings-dialog/task-settings-dialog.component";
import { TasksRootState, getAuthUser } from "../../store/index";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { ConfigService } from "../../../common/providers/config.service";
import { TaskService } from "../../task.service";
import { environment } from "../../../../environments/environment";
import { Broadcaster } from "../../../common/providers/broadcaster.service";
import { AppsDialogComponent } from "../../../shared/components/apps-dialog/apps.component.dialog";
import { Logout } from "../../../actions/app";
import { TaskGeneralSettingsDialogComponent } from "../task-settings/task-general-settings-dialog/task-general-settings.component";
import { TaskOptionListComponent } from "./task-option-list.component";
import { TaskLoginProfileDialogComponent } from "../task-login-user-profile/task-login-user-profile.component";
import { TagOptionListComponent } from "./tag-option-list.component";
import { TasksConstants } from "../../shared/task-constacts";
import { TaskRepository } from "../../repository/task.repository";
import { getOnlineStatus, getLoginStatus } from "../../../reducers";
import { FolderOptionListComponent } from "./folder-option-list.component";
import { CommonUtil } from "../../../common/utils/common.utils";
import { UsersManagementComponent } from "./users-management.component";
import { SaveSearchDialogComponent } from "../save-search-dialog/save-search-dialog.component";
import { LocationOptionListComponent } from "./location-option-list.component";
import { takeWhile, take } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { LogoutConfirmationComponent } from "../logout-confirmation";
import { UserAvatarComponent } from "../../../shared/components/user-avtar/user-avatar.component";
import { MatIcon } from "@angular/material/icon";
import { TranslatePipe } from "@ngx-translate/core";

@Component({
    selector: "vp-sidebar-drawer",
    template: `
  @if (showUserMenu) {
    <div class="mobile-sidebar-component">
      <div class="user-menu__mask" id="closeMainMenu" (click)="toggleDrawer(false)"></div>
      <div id="profileMenuMain" (click)="openProfileDialog()" class="profile__cover" [style.backgroundImage]="profileBackground">
        <vp-avatar [jid]="user?.email" [avatarId]="user.id" [user]="{ name: user && user.fullname ? user.fullname : '', avatarURL: user.userAvatar }"> </vp-avatar>
        <h4 id="userEmail" class="profile__h4 disable-select">{{user.email}}</h4>
      </div>
      <div class="drawer__links">
        @for (link of links; track link) {
          @if ((link.title !== 'USERS_MANAGEMENT') || (link.title === 'USERS_MANAGEMENT' && user.team_user === 'true')) {
            <div id="{{ link.id }}" class="__row" (click)="onAction(link)">
              <div class="left_col">
                @switch (link.icon) {
                  @case ('event_available') {
                    <mat-icon>{{link.icon}}</mat-icon>
                  }
                  @case ('youtube_searched_for') {
                    <mat-icon>{{link.icon}}</mat-icon>
                  }
                  @case ('apps') {
                    <mat-icon>{{link.icon}}</mat-icon>
                  }
                  @case ('assignment') {
                    <svg width="18px" height="18px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                      <title>Icon/product/assignment-24px</title>
                      <g id="Icon/product/assignment-24px" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <g id="Icon-24px" transform="translate(2.000000, 1.000000)" fill="#000000">
                          <path d="M16,2 L11.82,2 C11.4,0.84 10.3,0 9,0 C7.7,0 6.6,0.84 6.18,2 L2,2 C0.9,2 0,2.9 0,4 L0,18 C0,19.1 0.9,20 2,20 L16,20 C17.1,20 18,19.1 18,18 L18,4 C18,2.9 17.1,2 16,2 L16,2 Z M9,2 C9.55,2 10,2.45 10,3 C10,3.55 9.55,4 9,4 C8.45,4 8,3.55 8,3 C8,2.45 8.45,2 9,2 L9,2 Z M11,16 L4,16 L4,14 L11,14 L11,16 L11,16 Z M14,12 L4,12 L4,10 L14,10 L14,12 L14,12 Z M14,8 L4,8 L4,6 L14,6 L14,8 L14,8 Z" id="Shape"></path>
                        </g>
                      </g>
                    </svg>
                  }
                  @case ('place') {
                    <svg width="18px" height="18px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                      <title>Icon/product/location-new</title>
                      <g id="Icon/product/location-new" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <g id="Icon-24px" transform="translate(4.000000, 1.000000)" fill="#000000">
                          <path d="M8,0 C12.4228571,0 16,3.443 16,7.7 C16,9.65461538 15.0835503,11.9242604 13.8710168,14.0717708 L13.5334511,14.6540989 C13.476122,14.7505394 13.4182977,14.8466454 13.3600364,14.942376 L13.0056914,15.5119246 C12.9459153,15.6059889 12.8858188,15.6995955 12.8254602,15.7927035 L12.4606281,16.3450387 L12.0923004,16.8834349 L11.7232736,17.4059213 L11.3563441,17.9105274 L10.9943086,18.3952825 L10.6399636,18.8582157 L10.2961056,19.2973566 L8.76209811,21.1029683 C8.40451496,21.5238635 7.7734336,21.575188 7.35253844,21.2176049 C7.31128664,21.1825583 7.2729485,21.1442201 7.23790189,21.1029683 L5.7038944,19.2973566 L5.184,18.6296 L4.82546017,18.1555095 L4.46062813,17.6605826 L4.09230041,17.14679 C4.03077651,17.0596952 3.96922349,16.9718965 3.90769959,16.8834349 L3.53937187,16.3450387 L3.17453983,15.7927035 L2.816,15.2284 C2.69784615,15.0385231 2.5812071,14.8469799 2.46654893,14.6540989 L2.12898316,14.0717708 C0.916449704,11.9242604 0,9.65461538 0,7.7 C0,3.443 3.57714286,0 8,0 Z M8,2 C4.66497074,2 2,4.56503434 2,7.7 C2,8.69322797 2.3458444,9.96053154 3.01409327,11.4203421 C3.64939915,12.8081876 4.54667863,14.3111997 5.63770505,15.8676171 C6.30819597,16.8241143 7.01937804,17.7553435 7.73858591,18.6369503 L8,18.952 L8.26141409,18.6369503 C8.80081999,17.9757452 9.33571138,17.2866275 9.85230757,16.5798717 L10.362295,15.8676171 C11.4533214,14.3111997 12.3506009,12.8081876 12.9859067,11.4203421 C13.6541556,9.96053154 14,8.69322797 14,7.7 C14,4.56503434 11.3350293,2 8,2 Z M8,5.5 C9.38,5.5 10.5,6.62 10.5,8 C10.5,9.38 9.38,10.5 8,10.5 C6.62,10.5 5.5,9.38 5.5,8 C5.5,6.62 6.62,5.5 8,5.5 Z" id="Combined-Shape"></path>
                        </g>
                      </g>
                    </svg>
                  }
                  @case ('exit_to_app') {
                    <svg width="18px" height="18px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                      <title>Icon/product/exit-to-app</title>
                      <g id="Icon/product/exit-to-app" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <g id="Icon-24px" transform="translate(3.000000, 3.000000)" fill="#000000">
                          <path d="M16,0 C17.1,0 18,0.9 18,2 L18,2 L18,16 C18,17.1 17.1,18 16,18 L16,18 L2,18 C0.89,18 0,17.1 0,16 L0,16 L0,12 L2,12 L2,16 L16,16 L16,2 L2,2 L2,6 L0,6 L0,2 C0,0.9 0.89,0 2,0 L2,0 Z M8.5,4 L13.5,9 L8.5,14 L7.09,12.59 L9.668,10 L1,10 C0.44771525,10 0,9.55228475 0,9 C0,8.44771525 0.44771525,8 1,8 L9.67,8 L7.09,5.41 L8.5,4 Z" id="Shape"></path>
                        </g>
                      </g>
                    </svg>
                  }
                  @case ('info_outline') {
                    <svg width="18px" height="18px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                      <title>Icon/custom/info</title>
                      <g id="Icon/custom/info" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <g id="Icon/product/info-outline" transform="translate(2.000000, 2.000000)" fill="#000000">
                          <path d="M10,0 C15.52,0 20,4.48 20,10 C20,15.52 15.52,20 10,20 C4.48,20 0,15.52 0,10 C0,4.48 4.48,0 10,0 Z M10,2 C5.59,2 2,5.59 2,10 C2,14.41 5.59,18 10,18 C14.41,18 18,14.41 18,10 C18,5.59 14.41,2 10,2 Z M10,9 L10.1173326,9.00666569 C10.6172798,9.06389808 11,9.48267152 11,9.99077797 L11,9.99077797 L11,14.009222 C11,14.5490248 10.5522847,15 10,15 C9.44386482,15 9,14.5564136 9,14.009222 L9,14.009222 L9,9.99077797 C9,9.45097518 9.44771525,9 10,9 L10,9 Z M10,6 C10.5522847,6 11,6.44771525 11,7 C11,7.55228475 10.5522847,8 10,8 C9.44771525,8 9,7.55228475 9,7 C9,6.44771525 9.44771525,6 10,6 Z" id="Combined-Shape"></path>
                        </g>
                      </g>
                    </svg>
                  }
                  @case ('local_offer') {
                    <svg width="18px" height="18px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                      <title>Icon/product/tag-new</title>
                      <g id="Icon/product/tag-new" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <path d="M11,2 C11.5,2 11.9259259,2.14814815 12.2777778,2.44444444 L12.40625,2.5625 L21.40625,11.5625 C21.78125,11.9375 21.96875,12.40625 21.96875,12.96875 C21.96875,13.46875 21.8206019,13.8946759 21.5243056,14.2465278 L21.40625,14.375 L14.375,21.40625 C14,21.78125 13.53125,21.96875 12.96875,21.96875 C12.46875,21.96875 12.0428241,21.8206019 11.6909722,21.5243056 L11.5625,21.40625 L2.5625,12.40625 C2.22916667,12.0729167 2.04398148,11.6655093 2.00694444,11.1840278 L2,11 L2,3.96875 C2,3.40625 2.1875,2.9375 2.5625,2.5625 C2.89583333,2.22916667 3.30324074,2.04398148 3.78472222,2.00694444 L3.96875,2 L11,2 Z M11.013,3.997 L11,4 L4,4 L4,11 L3.997,11.013 L12.954,19.97 L12.96875,19.96875 L12.982,19.97 L19.97,12.982 L19.96875,12.96875 L19.97,12.954 L11.013,3.997 Z M6.4921875,5.015625 C6.9140625,5.015625 7.265625,5.15625 7.546875,5.4375 C7.828125,5.71875 7.96875,6.0703125 7.96875,6.4921875 C7.96875,6.9140625 7.828125,7.265625 7.546875,7.546875 C7.265625,7.828125 6.90625,7.96875 6.46875,7.96875 C6.0625,7.96875 5.71875,7.828125 5.4375,7.546875 C5.15625,7.265625 5.015625,6.9140625 5.015625,6.4921875 C5.015625,6.0703125 5.15625,5.71875 5.4375,5.4375 C5.71875,5.15625 6.0703125,5.015625 6.4921875,5.015625 Z" id="Combined-Shape" fill="#000000"></path>
                      </g>
                    </svg>
                  }
                  @case ('settings') {
                    <svg width="18px" height="18px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                      <title>Icon/product/settings-cogwheel-new</title>
                      <g id="Icon/product/settings-cogwheel-new" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <g id="icons8-settings" transform="translate(1.949973, 2.000000)" fill="#000000">
                          <path d="M11.4231169,0 C12.3000626,0 13.0570735,0.58680239 13.2914161,1.4195025 L13.329132,1.57843215 L13.5420268,2.683 L13.6650574,2.73907524 C13.9813101,2.88932322 14.2864682,3.05760158 14.5789259,3.24320599 L14.8670907,3.43455965 L14.9590268,3.501 L16.003358,3.13842368 C16.7817095,2.87000643 17.6299201,3.12461354 18.1367001,3.7447079 L18.2333818,3.87355326 L18.3202276,4.01186488 L19.7418746,6.49517926 C20.1755634,7.25415415 20.0518736,8.20455121 19.4535052,8.82422026 L19.3357687,8.93640501 L18.4940268,9.673 L18.5103644,9.82092448 L18.5235871,9.99238188 L18.5330889,10.1624713 L18.5407455,10.5 L18.5330889,10.8375287 L18.5103644,11.1790755 L18.4940268,11.326 L19.3358443,12.0636613 C19.9520376,12.6038435 20.155713,13.4620138 19.8783836,14.2107416 L19.816484,14.3589013 L19.7416359,14.5033826 L18.3197326,16.9889992 C17.8821398,17.7516186 16.9960025,18.1206089 16.1579005,17.9061517 L16.0014405,17.8590574 L14.9590268,17.497 L14.8662998,17.5645114 C14.5821878,17.7620332 14.285065,17.9423549 13.9765234,18.1047178 L13.6642346,18.2610663 L13.5420268,18.316 L13.3289531,19.422491 C13.1721398,20.2299003 12.533688,20.8406341 11.7455627,20.973161 L11.5860404,20.9932068 L11.4231169,21 L8.57693673,21 C7.6999911,21 6.94297978,20.4131974 6.70863709,19.580497 L6.67092119,19.4215673 L6.45702685,18.316 L6.33499718,18.2609248 C6.01874463,18.110677 5.71358654,17.9423987 5.42112868,17.7567944 L5.1329637,17.5654408 L5.04002685,17.498 L3.99669568,17.8615762 C3.21834427,18.1299932 2.37013355,17.8753867 1.86335353,17.2552925 L1.7666718,17.1264471 L1.67982607,16.9881355 L0.258177644,14.5048188 C-0.175509218,13.7458441 -0.0518194922,12.7954482 0.546548522,12.1757796 L0.664284971,12.0635949 L1.50502685,11.326 L1.4896893,11.1790756 L1.47646655,11.0076181 L1.46696476,10.8375287 L1.45930812,10.5 L1.46696476,10.1624713 L1.4896893,9.82092443 L1.50502685,9.673 L0.664209238,8.9363387 C0.0479825291,8.39612721 -0.155648164,7.53650303 0.121785066,6.78726916 L0.183706184,6.63902161 L0.258577394,6.4944819 L1.68084016,4.01009764 C2.11868439,3.24887871 3.00450919,2.87958065 3.84222646,3.09387639 L3.99861357,3.14094263 L5.04002685,3.502 L5.13375373,3.4354888 C5.41786569,3.23796707 5.71498836,3.05764544 6.02352983,2.89528252 L6.33581861,2.73893393 L6.45702685,2.683 L6.67110014,1.5775088 C6.82791343,0.770099483 7.46636563,0.159365846 8.25449093,0.0268390298 L8.41401327,0.0067931741 L8.57693673,0 L11.4231169,0 Z M11.3730268,2 L8.62602685,2 L8.214179,4.13348324 L7.69407503,4.33170275 C7.11358488,4.55293635 6.57573701,4.84767904 6.09424226,5.20839758 L5.85825445,5.39421996 L5.42459007,5.75300553 L3.39102685,5.047 L2.01102685,7.457 L3.64153921,8.88639906 L3.55330185,9.43292283 C3.49094437,9.81915194 3.45930812,10.1662481 3.45930812,10.5 C3.45930812,10.750314 3.47710351,11.008134 3.51230827,11.2843398 L3.55330185,11.5670772 L3.64153921,12.1136009 L2.01102685,13.541 L3.39102685,15.954 L5.4244994,15.2488815 L5.85814205,15.6075425 C6.31872892,15.9884888 6.83950417,16.3055062 7.40668241,16.5513433 L7.69407462,16.6682966 L8.21417897,16.866516 L8.62602685,19 L11.3730268,19 L11.7858751,16.8665157 L12.3059796,16.6682965 C12.886469,16.4470637 13.4243164,16.1523213 13.9058107,15.7916028 L14.1417983,15.6057805 L14.5754626,15.2469946 L16.6080268,15.952 L17.9870268,13.541 L16.3585147,12.113601 L16.4467519,11.5670774 C16.5091093,11.1808484 16.5407455,10.8337521 16.5407455,10.5 C16.5407455,10.2496859 16.5229501,9.99186575 16.4877455,9.71565993 L16.4467519,9.4329226 L16.3585147,8.88639904 L17.9880268,7.458 L16.6080268,5.045 L14.5755533,5.7511187 L14.1419105,5.392457 C13.6813248,5.01151103 13.1605501,4.69449379 12.5933714,4.4486563 L12.3059789,4.33170278 L11.785875,4.13348338 L11.3730268,2 Z M10.0500268,6.5 C12.2596835,6.5 14.0500268,8.29531651 14.0500268,10.5083333 C14.0500268,12.7213502 12.2596835,14.5166667 10.0500268,14.5166667 C7.84037018,14.5166667 6.05002685,12.7213502 6.05002685,10.5083333 C6.05002685,8.29531651 7.84037018,6.5 10.0500268,6.5 Z M10.0500268,8.5 C8.94618351,8.5 8.05002685,9.39864599 8.05002685,10.5083333 C8.05002685,11.6180207 8.94618351,12.5166667 10.0500268,12.5166667 C11.1538702,12.5166667 12.0500268,11.6180207 12.0500268,10.5083333 C12.0500268,9.39864599 11.1538702,8.5 10.0500268,8.5 Z" id="Shape"></path>
                        </g>
                      </g>
                    </svg>
                  }
                }
                <span class="disable-select">{{link.title | translate}}</span>
              </div>
            </div>
          }
        }
      </div>
    </div>
  }
  `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [UserAvatarComponent, MatIcon, TranslatePipe]
})
export class SidebarDrawerComponent implements OnDestroy{
  private headerService = inject(HeaderService);
  private store = inject<Store<TasksRootState>>(Store);
  private router = inject(Router);
  private configService = inject(ConfigService);
  private taskService = inject(TaskService);
  private broadcaster = inject(Broadcaster);
  private changerDetectorRef = inject(ChangeDetectorRef);

  private tasksRepo = inject(TaskRepository);
  private matDialog = inject(MatDialog);
  user: any = {};
  @Input() showUserMenu: boolean = false;
  profileBackground = "url(" + CommonUtil.getFullUrl("/assets/img/profile-background.jpg") + ")";
  @Input() url: string;
  @ViewChild(TagOptionListComponent, { static: false }) tagOptionListDialog: TagOptionListComponent;
  switchApp: boolean = false;
  isAlive: boolean = true;
  isOnline: boolean = false;
  actions: any = {
    APP_SWITCHER: "APP_SWITCHER",
    TASK_LIST: "TASK_LIST",
    FOLDER_LIST: "FOLDER_LIST",
    TAG_LIST: "TAG_LIST",
    LOCATION_LIST: "LOCATION_LIST",
    TASK_GENERAL_SETTINGS: "TASK_GENERAL_SETTINGS",
    ABOUT: "ABOUT",
    TASK_SETTINGS: "TASK_SETTINGS",
    LOGOUT: "LOGOUT",
    PROFILE: "PROFILE",
    USERS_MANAGEMENT: "USERS_MANAGEMENT",
    SAVED_SEARCHES: "SAVED_SEARCHES"
  };
  links: Link[] = [
    {
      title: "APPS",
      action: "APP_SWITCHER",
      icon: "apps",
      id: "appSwitchBtn"
    },
    {
      title: "TASKS",
      action: "TASK_LIST",
      icon: "event_available",
      enableRoutes: ["task"],
      id: "tasksBtn"
    },
    {
      title: "LISTS",
      action: "FOLDER_LIST",
      icon: "assignment",
      enableRoutes: ["task"],
      id: "listsBtn"
    },
    {
      title: "TAGS",
      action: "TAG_LIST",
      icon: "local_offer",
      enableRoutes: ["task"],
      id: "tagsBtn"
    },
    {
      title: "LOCATIONS",
      action: "LOCATION_LIST",
      icon: "place",
      enableRoutes: ["task"],
      id: "locationsBtn"
    },
    {
      title: "USERS_MANAGEMENT",
      action: "USERS_MANAGEMENT",
      icon: "account_box",
      enableRoutes: ["task"],
      id: "userMenagementBtn"
    },
    {
      title: "SAVED_SEARCHES",
      action: "SAVED_SEARCHES",
      icon: "youtube_searched_for",
      enableRoutes: ["task"],
      id: "savedSearchesBtn"
    },
    {
      title: "TASK_GENERAL_SETTINGS",
      action: "TASK_GENERAL_SETTINGS",
      icon: "settings",
      enableRoutes: ["task"],
      id: "generalSettingMenu"
    },
    {
      title: "ABOUT",
      action: "ABOUT",
      icon: "info_outline",
      enableRoutes: ["task"],
      id: "aboutMenu"
    },
    {
      title: "LOGOUT",
      action: "LOGOUT",
      icon: "exit_to_app",
      id: "logoutBtn"
    }
  ];

  constructor() {

    this.store.select(getAuthUser).pipe(takeWhile(() => this.isAlive)).subscribe(user => {
      if (user) {
        this.user = user;
        this.changerDetectorRef.markForCheck();
      }
    });

    this.store.select(getOnlineStatus).pipe(takeWhile(() => this.isAlive)).subscribe((isOnline) => {
      this.isOnline = isOnline;
      this.changerDetectorRef.markForCheck();
    });

    this.broadcaster.on<any>("profileAvatarUpdated").pipe(takeWhile(() => this.isAlive))
    .subscribe(presence => {
      this.tasksRepo.getAllUsers();
      this.updateUserAvatar();
    });

    this.broadcaster.on<any>("closeLayoutDrawer").pipe(takeWhile(() => this.isAlive))
      .subscribe(presence => {
        this.headerService.toggleDrawer$.next(false);
      });
  }

  private updateUserAvatar() {
    this.store.select(getAuthUser).pipe(take(1)).subscribe(user => {
      if (user) {
        this.user = user;
        this.changerDetectorRef.markForCheck();
      }
    });
  }

  toggleDrawer(value: boolean): void {
    this.headerService.toggleDrawer(value);
  }

  onAction(link: any): void {
    switch (link.action) {
      case this.actions.APP_SWITCHER:
        const appDlg = this.matDialog.open(AppsDialogComponent, {
          maxWidth: "100%",
          autoFocus: false,
          panelClass: "vp-apps-dialog",
          data: { url: this.url }
        });
        break;

      case this.actions.TASK_GENERAL_SETTINGS:
        const gdlg = this.matDialog.open(TaskGeneralSettingsDialogComponent, {
          maxWidth: "100%",
          autoFocus: false,
          panelClass: "vnctaskgeneralsettingsdialog",
        });
        break;

      case this.actions.TASK_LIST:
        const dlg = this.matDialog.open(TaskOptionListComponent, {
          maxWidth: "100%",
          autoFocus: false,
          panelClass: "vp-task-option-list-dialog",
        });
        dlg.afterClosed().pipe(take(1)).subscribe($event => {
          if ($event && $event.title) {
            this.navigateRoute($event);
          }
        });
        break;

      case this.actions.FOLDER_LIST:
        const fDialog = this.matDialog.open(FolderOptionListComponent, {
          maxWidth: "100%",
          autoFocus: false,
          panelClass: "vp-folder-option-list-dialog",
        });
        fDialog.afterClosed().pipe(take(1)).subscribe($event => {
          if ($event && $event.title) {
            this.navigateRoute($event);
          }
        });
        break;

      case this.actions.TAG_LIST:
        const tDialog = this.matDialog.open(TagOptionListComponent, {
          maxWidth: "100%",
          autoFocus: false,
          panelClass: "vp-tag-option-list-dialog",
        });
        tDialog.afterClosed().pipe(take(1)).subscribe($event => {
          if ($event && $event.title) {
            this.navigateRoute($event);
          }
        });
        break;

      case this.actions.LOCATION_LIST:
        const lDialog = this.matDialog.open(LocationOptionListComponent, {
          maxWidth: "100%",
          autoFocus: false,
          panelClass: "vp-location-option-list-dialog",
        });
        lDialog.afterClosed().pipe(take(1)).subscribe($event => {
          if ($event && $event.title) {
            this.navigateRoute($event);
          }
        });
        break;

      case this.actions.USERS_MANAGEMENT:
        this.matDialog.open(UsersManagementComponent, {
          maxWidth: "100%",
          autoFocus: false,
          panelClass: "vp-users-management-dialog"
        });
        break;

      case this.actions.SAVED_SEARCHES:
        const sDialog = this.matDialog.open(SaveSearchDialogComponent, {
          maxWidth: "100%",
          autoFocus: false,
          panelClass: "vp-save-search-dialog",
        });
        sDialog.afterClosed().pipe(take(1)).subscribe($event => {
          if ($event && $event.query) {
            this.navigateSaveSearches($event.query);
          }
        });
        break;
      case this.actions.ABOUT:
        const aDialog = this.matDialog.open(TaskSettingsDialogComponent, {
          maxWidth: "100%",
          autoFocus: false,
          panelClass: "vnctasksettingsdialog",
        });
        break;

      case this.actions.LOGOUT:
        this.headerService.toggleDrawer$.next(false);
        this.logout();
        break;
    }
  }

  navigateRoute($event) {
    console.log("SidebarDrawerComponent, navigateRoute: ", $event);

    this.headerService.toggleDrawer$.next(false);
    if ( $event.type === TasksConstants.ROUTE_TYPE_STATUS ) {
      this.router.navigate(["/task", $event.title]);
    } else if ( $event.type === TasksConstants.ROUTE_TYPE_TAG ) {
      this.router.navigate(["/task/tags", $event.title]);
    } else if ( $event.type === TasksConstants.ROUTE_TYPE_LIST ){
      this.router.navigate(["/task/list", $event.title]);
    } else {
      this.router.navigate(["/task/location", $event.title]);
    }
  }

  navigateSaveSearches(query) {
    this.tasksRepo.setSearchParams(query);
    this.router.navigate(["/task", TasksConstants.ROUTE_SEARCH], { queryParams: {} });
  }

  logoutConfirm() {
    if (environment.isCordova && typeof window.FirebasePlugin !== "undefined") {
      window.FirebasePlugin.getToken(token => {
        this.taskService.removeFirebaseToken(token)
          .pipe(take(1))
          .subscribe(res => {
            console.log("[removeFirebaseToken]", token, res);
            window.FirebasePlugin.unregister();
            this.processLogout();
          }, err => {
            console.error("[removeFirebaseToken - error]", err);
            this.processLogout();
          });
      });

      window.FirebasePlugin.clearAllNotifications( () => {
        console.log("[FirebasePlugin clearAllNotifications] success");
      }, error => {
        console.log("[FirebasePlugin clearAllNotifications] error", error);
      });
    } else {
      this.processLogout();
    }
  }

  logout(): void {
    let options: any = {
      width: "328px",
      height: "280px",
    };

    const dlg = this.matDialog.open(LogoutConfirmationComponent, Object.assign({
      backdropClass: ["vncMail-form-backdrop", "mail-logout-backdrop"],
      panelClass: ["vncmail-form-panel-style", "mail-logout-mobile-view"],
      disableClose: true,
      data: {
      },
      autoFocus: true
     }, options));

     dlg.afterClosed().pipe(take(1)).subscribe(res => {
       if (res) {
         this.logoutConfirm();
       }
    });

  }

  private processLogout(): void {
    this.tasksRepo.logout();
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }

  openProfileDialog() {
    const gdlg = this.matDialog.open(TaskLoginProfileDialogComponent, {
      maxWidth: "100%",
      autoFocus: false,
      panelClass: "vnc_task_login_profile_dialog",
    });
  }
}

interface Link {
  title: string;
  action: string;
  icon: string;
  id?: string;
  enabled?: boolean;
  enableRoutes?: string[];
  enableBadge?: boolean;
  badgeValue?: string;
  isFontAwesome?: boolean;
  children?: any[];
  expanded?: boolean;
}
